/**
 * 文件传输管理
 * 负责 P2P 模式下的文件分片、传输控制、进度跟踪
 */
export class FileTransfer {
  constructor(options = {}) {
    this.webrtc = options.webrtc || null;
    this.chunkSize = options.chunkSize || 262144; // 256KB

    // 文件信息
    this.file = null;
    this.fileName = '';
    this.fileSize = 0;
    this.totalChunks = 0;
    this.transferredChunks = 0;

    // 接收方
    this.receivedChunks = new Set();
    this.chunkBuffers = [];
    this._fileCompleted = false;
    this._fileAckResolve = null;
    this._pendingFileDoneIndex = null;
    this.currentReceiveFile = null;

    this.isTransferring = false;

    // 回调
    this.onProgress = options.onProgress || (() => {});
    this.onComplete = options.onComplete || (() => {});
    this.onError = options.onError || (() => {});
    this.onProcessing = options.onProcessing || (() => {});
  }

  /**
   * 设置要发送的文件
   */
  setFile(file) {
    this.file = file;
    this.fileName = file.name;
    this.fileSize = file.size;
    this.totalChunks = Math.ceil(file.size / this.chunkSize);
    this.transferredChunks = 0;
  }

  /**
   * 开始发送
   */
  async startSend() {
    if (!this.file || !this.webrtc) {
      this.onError('文件或 WebRTC 连接未就绪');
      return;
    }

    // 等待数据通道就绪
    if (!this.webrtc.isDataChannelReady) {
      for (let i = 0; i < 100; i++) {
        await this._sleep(100);
        if (this.webrtc.isDataChannelReady) break;
      }
      if (!this.webrtc.isDataChannelReady) {
        this.onError('数据通道未就绪');
        return;
      }
    }

    // 动态调整分片大小
    const maxMsgSize = this.webrtc.maxMessageSize || 262144;
    this.chunkSize = maxMsgSize - 4;

    this.isTransferring = true;
    this.allChunksSent = false;  // 标记所有分片是否已发送完

    // 发送传输开始消息
    this.webrtc.sendControl({
      type: 'transfer_start',
      isFolder: false,
      totalFiles: 1,
      totalSize: this.fileSize,
      fileList: [{ name: this.fileName, path: this.fileName, size: this.fileSize }]
    });

    // 发送文件开始消息
    this.webrtc.sendControl({
      type: 'start',
      fileIndex: 0,
      fileName: this.fileName,
      filePath: this.fileName,
      fileSize: this.fileSize,
      totalChunks: this.totalChunks
    });

    // 发送分片
    await this._sendChunks();

    if (!this.isTransferring) return;

    // 标记所有分片已发送完
    this.allChunksSent = true;

    // 分片已全部发送，显示等待文案
    this.onProcessing({ message: '正在等待接收方确认...', phase: 'waiting_ack' });

    // 立即发送file_done（不带哈希，不阻塞）
    this.webrtc.sendControl({ type: 'file_done', fileIndex: 0, fileHash: '' });

    // 哈希算完后通过单独消息补发（异步，不阻塞）
    this._sendHashWhenReady(0);

    // 等待接收方确认
    await this._waitForFileAck();

    if (this.isTransferring) {
      this.webrtc.sendControl({ type: 'done' });
      this.isTransferring = false;
      this.onComplete({ fileName: this.fileName, fileSize: this.fileSize });
    }
  }

  /**
   * 哈希算完后通过 file_hash 消息补发给接收方
   * 异步执行，不阻塞发送方主流程
   */
  _sendHashWhenReady(fileIndex) {
    // SDK默认不计算哈希，由调用者通过setFileHash设置
    // 如果调用者提供了哈希计算函数，在此调用
    if (this._hashPromise) {
      this._hashPromise.then(hash => {
        if (hash && this.isTransferring && this.webrtc) {
          this.webrtc.sendControl({ type: 'file_hash', fileIndex, fileHash: hash });
        }
      }).catch(() => {});
    }
  }

  /**
   * 分片发送
   */
  async _sendChunks() {
    let nextChunkPromise = this._readChunk(0);

    for (let i = 0; i < this.totalChunks; i++) {
      if (!this.isTransferring) break;

      const fileData = await nextChunkPromise;
      if (i + 1 < this.totalChunks) {
        nextChunkPromise = this._readChunk(i + 1);
      }

      const header = new ArrayBuffer(4);
      new DataView(header).setUint32(0, i, true);
      const packet = this._concatBuffers([header, fileData]);

      const sent = await this.webrtc.sendData(packet);
      if (sent) {
        this.transferredChunks = i + 1;
        const percent = (this.transferredChunks / this.totalChunks) * 100;
        this.onProgress(percent);
      } else {
        this.isTransferring = false;
        this.onError('数据通道已断开');
        break;
      }
    }
  }

  /**
   * 读取指定分片
   */
  async _readChunk(index) {
    const start = index * this.chunkSize;
    const end = Math.min(start + this.chunkSize, this.fileSize);
    const blob = this.file.slice(start, end);
    return await blob.arrayBuffer();
  }

  /**
   * 处理接收到的数据分片
   */
  handleReceive(data) {
    if (data instanceof ArrayBuffer && data.byteLength > 4) {
      const chunkIndex = new DataView(data).getUint32(0, true);
      const fileData = data.slice(4);
      this.chunkBuffers[chunkIndex] = fileData;
      this.receivedChunks.add(chunkIndex);

      const percent = (this.receivedChunks.size / this.totalChunks) * 100;
      this.onProgress(percent);

      // 分片全部到齐则完成
      if (!this._fileCompleted && this.totalChunks > 0 && this.receivedChunks.size >= this.totalChunks) {
        this._fileCompleted = true;
        this.onProcessing({ message: '正在处理文件...' });
        this._completeCurrentFile();
      }
    }
  }

  /**
   * 处理控制消息
   */
  handleControlMessage(message) {
    switch (message.type) {
      case 'transfer_start':
        this.isTransferring = true;
        break;

      case 'start':
        this.fileName = message.fileName;
        this.fileSize = message.fileSize;
        this.totalChunks = message.totalChunks;
        this.chunkBuffers = [];
        this.receivedChunks = new Set();
        this._fileCompleted = false;
        this._pendingFileDoneIndex = null;
        this.currentReceiveFile = {
          fileName: message.fileName,
          filePath: message.filePath || message.fileName,
          fileSize: message.fileSize,
          totalChunks: message.totalChunks,
          fileHash: ''
        };
        this.isTransferring = true;
        break;

      case 'file_done':
        // 发送方告知当前文件已发完（哈希可能为空，通过file_hash补发）
        if (this._fileCompleted) {
          this._sendFileAck(message.fileIndex);
        } else if (this.receivedChunks.size >= this.totalChunks) {
          this._fileCompleted = true;
          this._completeCurrentFile();
          this._sendFileAck(message.fileIndex);
        } else {
          this._pendingFileDoneIndex = message.fileIndex;
        }
        break;

      case 'file_hash':
        // 发送方补发的文件哈希
        if (this.currentReceiveFile) {
          this.currentReceiveFile.fileHash = message.fileHash || '';
        }
        // 如果接收方正在等待哈希，resolve它
        if (this._hashWaitResolve) {
          if (this._hashWaitTimer) clearTimeout(this._hashWaitTimer);
          const resolve = this._hashWaitResolve;
          this._hashWaitResolve = null;
          this._hashWaitTimer = null;
          resolve(message.fileHash || '');
        }
        break;

      case 'file_ack':
        if (this._fileAckResolve) {
          this._fileAckResolve();
          this._fileAckResolve = null;
        }
        break;

      case 'done':
        // 所有文件接收完成，发送最终ack给发送方
        if (this.webrtc) {
          this.webrtc.sendControl({ type: 'ack', status: 'success' });
        }
        this.isTransferring = false;
        break;
    }
  }

  /**
   * 组装已接收的文件
   * 
   * 方案A流程（先校验再下载）：
   * 1. 分片到齐后合并为Blob
   * 2. 等待发送方的file_hash消息到达（最多5秒超时）
   * 3. 哈希校验通过后通过onComplete返回blob，失败调用onError
   * 
   * SDK不在内部做哈希校验，通过onComplete返回blob和fileHash供调用者校验
   */
  async _completeCurrentFile() {
    if (this.receivedChunks.size < this.totalChunks) {
      this.onError(`文件接收不完整 (${this.receivedChunks.size}/${this.totalChunks})`);
      return;
    }

    const orderedBuffers = [];
    for (let i = 0; i < this.totalChunks; i++) {
      if (this.chunkBuffers[i]) orderedBuffers.push(this.chunkBuffers[i]);
    }
    const blob = new Blob(orderedBuffers, { type: 'application/octet-stream' });

    // 等待发送方的file_hash到达
    let fileHash = this.currentReceiveFile ? this.currentReceiveFile.fileHash : '';
    if (!fileHash) {
      fileHash = await this._waitForFileHash(5000);
    }

    // 清理缓冲
    this.chunkBuffers = [];
    this.receivedChunks = new Set();

    this.isTransferring = false;

    // 返回blob和fileHash，由调用者处理下载和校验
    this.onComplete({
      fileName: this.fileName,
      fileSize: blob.size,
      blob,
      fileHash
    });

    // 如果 file_done 已经到了，发送 ack
    if (this._pendingFileDoneIndex !== undefined && this._pendingFileDoneIndex !== null) {
      this._sendFileAck(this._pendingFileDoneIndex);
      this._pendingFileDoneIndex = null;
    }
  }

  /**
   * 等待发送方的file_hash消息到达
   */
  _waitForFileHash(timeout) {
    return new Promise((resolve) => {
      this._hashWaitResolve = resolve;
      const timer = setTimeout(() => {
        if (this._hashWaitResolve === resolve) {
          this._hashWaitResolve = null;
          resolve('');
        }
      }, timeout);
      this._hashWaitTimer = timer;
    });
  }

  _sendFileAck(fileIndex) {
    if (this.webrtc) {
      this.webrtc.sendControl({ type: 'file_ack', fileIndex });
    }
  }

  _waitForFileAck() {
    return new Promise((resolve) => {
      this._fileAckResolve = resolve;
      setTimeout(() => {
        if (this._fileAckResolve === resolve) {
          this._fileAckResolve = null;
          resolve();
        }
      }, 10000);
    });
  }

  cancel() {
    this.isTransferring = false;
  }

  _concatBuffers(buffers) {
    let totalLen = 0;
    for (const buf of buffers) totalLen += buf.byteLength;
    const result = new Uint8Array(totalLen);
    let offset = 0;
    for (const buf of buffers) {
      result.set(new Uint8Array(buf), offset);
      offset += buf.byteLength;
    }
    return result.buffer;
  }

  _sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
